import { useMemo, useState } from "react";
import {
  ArrowDownLeft, ArrowUpRight, Receipt, ClipboardList, Car, CalendarClock,
  Search, ChevronRight, Wallet, CalendarDays, Wrench,
} from "lucide-react";
import { money, can } from "../lib/api";
import { DailyReportButton } from "./DailyReport";

export default function MobileHome({ user, dash, customers, setPage, setModal }) {
  const [q, setQ] = useState("");
  const todayStr = new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" });

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const n = q.toLowerCase();
    return customers
      .filter((c) => (c.name || "").toLowerCase().includes(n) || (c.tax_no || "").includes(q.trim()) || (c.tc_no || "").includes(q.trim()))
      .slice(0, 6);
  }, [q, customers]);

  const actions = [
    { label: "Fatura Kes", icon: Receipt, color: "#0891b2", testid: "mhome-add-invoice",
      fn: () => setModal({ type: "invoice" }) },
    { label: "İş Fişi", icon: ClipboardList, color: "#ea580c", testid: "mhome-add-workorder",
      fn: () => setModal({ type: "work-order" }) },
    { label: "Randevu", icon: CalendarDays, color: "#0891b2", testid: "mhome-appointments",
      fn: () => setPage("appointments") },
    { label: "Servis", icon: Wrench, color: "#7c3aed", testid: "mhome-service",
      fn: () => setPage("service-tickets") },
    { label: "Ruhsat Arşivi", icon: Car, color: "#0f172a", testid: "mhome-ruhsat-archive",
      fn: () => setPage("ruhsat-archive") },
    { label: "Ödemeler", icon: CalendarClock, color: "#ca8a04", testid: "mhome-payments",
      fn: () => setPage("payments") },
  ];

  return (
    <div className="mobile-home" data-testid="mobile-home">
      <div className="mh-greet">
        <div className="eyebrow">{todayStr}</div>
        <h1>Merhaba, {user.name}.</h1>
        <p>İşletmenizin güncel görünümü ve hızlı işlemler.</p>
      </div>
      {can(user, "view_daily_report") && <div className="mh-report"><DailyReportButton className="primary-button" /></div>}

      <div className="mh-kpis" data-testid="mhome-kpis">
        <div className="mh-kpi" data-testid="mhome-kpi-balance">
          <small><Wallet size={11} /> Net bakiye</small>
          <strong>{money(dash.balance)}</strong>
        </div>
        <div className="mh-kpi pos" data-testid="mhome-kpi-income">
          <small><ArrowDownLeft size={11} /> Bu ay gelir</small>
          <strong>{money(dash.income)}</strong>
        </div>
        <div className="mh-kpi neg" data-testid="mhome-kpi-expense">
          <small><ArrowUpRight size={11} /> Bu ay gider</small>
          <strong>{money(dash.expense)}</strong>
        </div>
      </div>

      {(dash.pending_work_orders > 0 || dash.upcoming > 0) && (
        <div className="mh-chips">
          {dash.pending_work_orders > 0 && (
            <button className="mh-chip" data-testid="mhome-chip-workorders" onClick={() => setPage("work-orders")}>
              <ClipboardList size={16} /> Bekleyen iş fişleri
              <b>{dash.pending_work_orders}</b>
              <ChevronRight size={14} />
            </button>
          )}
          {dash.upcoming > 0 && (
            <button className="mh-chip" data-testid="mhome-chip-upcoming" onClick={() => setPage("payments")}>
              <CalendarClock size={16} /> Yaklaşan / bekleyen ödemeler
              <b>{dash.upcoming}</b>
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      )}

      {(dash.appointments_today || []).length > 0 && (
        <section className="mh-today-apt" data-testid="mhome-today-appointments">
          <div className="mh-today-head">
            <h2 className="mh-section-title" style={{ margin: 0 }}>Bugünün Randevuları</h2>
            <button className="mh-today-link" data-testid="mhome-today-all"
                    onClick={() => setPage("appointments")}>Takvimi aç <ChevronRight size={13} /></button>
          </div>
          <div className="mh-today-list">
            {dash.appointments_today.slice(0, 4).map((a) => (
              <button key={a.id} className="mh-today-item" data-testid={`mhome-today-${a.id}`}
                      onClick={() => setPage("appointments")}>
                <span className="mh-today-time"><CalendarDays size={13} /> {a.time || "--:--"}</span>
                <div className="mh-today-body">
                  <strong>{a.title || a.customer_name || "Randevu"}</strong>
                  <small>{a.customer_name || "—"}{a.plate ? ` · ${a.plate}` : ""}</small>
                </div>
                <span className={`status-pill ${a.status === "Tamamlandı" ? "paid" : "pending"}`}>{a.status || "Planlandı"}</span>
              </button>
            ))}
            {dash.appointments_today.length > 4 && (
              <button className="mh-today-link" style={{ alignSelf: "center" }}
                      onClick={() => setPage("appointments")}>+{dash.appointments_today.length - 4} randevu daha</button>
            )}
          </div>
        </section>
      )}

      <h2 className="mh-section-title">Hızlı İşlemler</h2>
      <div className="mh-actions">
        {actions.map((a) => (
          <button key={a.testid} className="mh-action" data-testid={a.testid} onClick={a.fn}>
            <span className="mh-ico" style={{ background: a.color }}><a.icon size={20} /></span>
            {a.label}
          </button>
        ))}
      </div>

      <h2 className="mh-section-title">Cari Bakiye Sorgula</h2>
      <div className="mh-search">
        <Search size={15} />
        <input data-testid="mhome-customer-search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Müşteri adı veya vergi/TC no yazın" />
      </div>
      {q.trim() && results.length === 0 && <p className="muted" data-testid="mhome-customer-empty">Eşleşen cari bulunamadı.</p>}
      <div className="mh-cari-list">
        {results.map((c) => (
          <div key={c.id} className="mh-cari-row" data-testid={`mhome-customer-${c.id}`}>
            <button className="mh-cari-top" onClick={() => setModal({ type: "statement", customerId: c.id })}
                    data-testid={`mhome-customer-open-${c.id}`}>
              <strong>{c.name}</strong>
              <span className={`bal ${c.balance > 0.01 ? "borc" : c.balance < -0.01 ? "alacak" : ""}`}>
                {Math.abs(c.balance || 0) < 0.01 ? "0,00 ₺" : `${money(Math.abs(c.balance))} ${c.balance > 0 ? "borç" : "alacak"}`}
              </span>
            </button>
            <div className="mh-cari-actions">
              <button className="mh-tahsilat" data-testid={`mhome-tahsilat-${c.id}`}
                      onClick={() => setModal({ type: "quick-payment", customer: c, direction: "in" })}>
                <ArrowDownLeft size={13} /> Tahsilat
              </button>
              <button className="mh-odeme" data-testid={`mhome-odeme-${c.id}`}
                      onClick={() => setModal({ type: "quick-payment", customer: c, direction: "out" })}>
                <ArrowUpRight size={13} /> Ödeme
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
